import React, { useEffect, useRef } from 'react';
import type { InboxEmailListItem as InboxItem } from '../../lib/inboxApi';
import { InboxEmailListItem } from './InboxEmailListItem';

interface Props {
  items: InboxItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  total?: number;
}

export const InboxEmailList: React.FC<Props> = ({
  items,
  selectedId,
  onSelect,
  onLoadMore,
  hasMore,
  loadingMore,
  total,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const thresholdPx = 180;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < thresholdPx && hasMore && !loadingMore) {
      onLoadMore();
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const canScroll = el.scrollHeight > el.clientHeight + 8;
    if (!canScroll && hasMore && !loadingMore) {
      onLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, hasMore]);

  if (!items.length) {
    return (
      <div className="h-full flex items-center justify-center px-4">
        <div className="text-center text-xs text-slate-500">
          <div className="mb-1 font-semibold text-slate-600">No emails found</div>
          <div className="text-xs text-slate-400">
            Try changing the category or search term.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto divide-y divide-slate-100"
      >
        {items.map((item) => (
          <InboxEmailListItem
            key={item._id}
            item={item}
            selected={selectedId === item._id}
            onClick={() => onSelect(item._id)}
          />
        ))}

        <div className="px-4 py-3 text-center">
          {loadingMore ? (
            <div className="text-xs text-slate-500">Loading more…</div>
          ) : hasMore ? (
            <button
              type="button"
              onClick={onLoadMore}
              className="text-xs font-semibold text-primary-500 hover:underline"
            >
              Load more
            </button>
          ) : (
            <div className="text-xs text-slate-400">End of list</div>
          )}
        </div>
      </div>
    </div>
  );
};
